import { API_BASE_URL, fetchApi } from '@/services/api-client';

export const STUDENT_SUSPENSIONS_PATH = '/student-suspensions';

export type StudentSuspensionStatus = 'active' | 'cancelled';
export type StudentSuspensionServedAt = 'school' | 'home';
export type StudentSuspensionDayOutcome = 'pending' | 'served' | 'not_served' | 'absent';
/** Which slice of the list to fetch; `cancelled` is a status, the rest are dates around today. */
export type StudentSuspensionWhen = 'current' | 'upcoming' | 'past';

export type StudentSuspensionPerson = {
  id: string;
  displayName: string;
  fullName: string | null;
  gradeLevel: number | null;
  photoUrl: string;
};

export type StudentSuspensionDay = {
  id: string;
  date: string;
  outcome: StudentSuspensionDayOutcome;
  note: string | null;
  markedBy: StudentSuspensionPerson | null;
  markedAt: string | null;
};

export type StudentSuspensionNote = {
  id: string;
  body: string;
  author: StudentSuspensionPerson | null;
  createdAt: string;
};

export type StudentSuspension = {
  id: string;
  student: StudentSuspensionPerson;
  schoolYearId: string;
  startDate: string;
  endDate: string;
  servedAt: StudentSuspensionServedAt;
  location: string | null;
  /** Who teachers send work to. */
  supervisor: StudentSuspensionPerson | null;
  edpAllowed: boolean;
  incidentId: string | null;
  /** SST only; null for every other staff viewer. */
  reason: string | null;
  status: StudentSuspensionStatus;
  createdBy: StudentSuspensionPerson | null;
  cancelledBy: StudentSuspensionPerson | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** SST only — absent, not empty, for everyone else. */
  days?: StudentSuspensionDay[];
  /** SST only — absent, not empty, for everyone else. */
  notes?: StudentSuspensionNote[];
};

export type StudentSuspensionFilters = {
  status?: StudentSuspensionStatus;
  studentId?: string;
  when?: StudentSuspensionWhen;
};

export type StudentSuspensionThreadEntry = {
  teacherId: string;
  teacher: StudentSuspensionPerson;
  /** Null until the first post on either side creates the thread. */
  threadId: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  /** False only for the SST, on a teacher who has since stopped teaching the student. */
  inAudience: boolean;
};

export type StudentSuspensionAttachment = {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
};

export type StudentSuspensionMessage = {
  id: string;
  body: string;
  author: StudentSuspensionPerson | null;
  attachments: StudentSuspensionAttachment[];
  createdAt: string;
};

export type CreateStudentSuspensionData = {
  studentId: string;
  startDate: string;
  endDate: string;
  servedAt: StudentSuspensionServedAt;
  location?: string | null;
  supervisorId?: string | null;
  edpAllowed?: boolean;
  incidentId?: string | null;
  reason: string;
};

export type UpdateStudentSuspensionData = Partial<Omit<CreateStudentSuspensionData, 'studentId'>>;

export type MarkStudentSuspensionDayData = {
  outcome: StudentSuspensionDayOutcome;
  note?: string | null;
};

/** Mirrors the API's multipart limits so a bad pick is refused here, before the upload. */
export const SUSPENSION_ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
export const SUSPENSION_ATTACHMENT_MAX_FILES = 6;
export const SUSPENSION_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

const threadPath = (id: string, teacherId: string) => `${STUDENT_SUSPENSIONS_PATH}/${id}/threads/${teacherId}`;

export function studentSuspensionAttachmentUrl(id: string, teacherId: string, attachmentId: string): string {
  return `${API_BASE_URL}${threadPath(id, teacherId)}/attachments/${attachmentId}`;
}

export const studentSuspensionsApi = {
  list: (filters: StudentSuspensionFilters = {}) => {
    const qs = new URLSearchParams();
    if (filters.status) qs.append('status', filters.status);
    if (filters.studentId) qs.append('studentId', filters.studentId);
    if (filters.when) qs.append('when', filters.when);
    const s = qs.toString();
    return fetchApi<StudentSuspension[]>(`${STUDENT_SUSPENSIONS_PATH}${s ? `?${s}` : ''}`);
  },
  mine: () => fetchApi<StudentSuspension[]>(`${STUDENT_SUSPENSIONS_PATH}/mine`),
  unreadCount: () => fetchApi<{ count: number }>(`${STUDENT_SUSPENSIONS_PATH}/unread-count`),
  get: (id: string) => fetchApi<StudentSuspension>(`${STUDENT_SUSPENSIONS_PATH}/${id}`),
  create: (data: CreateStudentSuspensionData) =>
    fetchApi<StudentSuspension>(STUDENT_SUSPENSIONS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateStudentSuspensionData) =>
    fetchApi<StudentSuspension>(`${STUDENT_SUSPENSIONS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancel: (id: string) => fetchApi<StudentSuspension>(`${STUDENT_SUSPENSIONS_PATH}/${id}/cancel`, { method: 'POST' }),
  markDay: (id: string, date: string, data: MarkStudentSuspensionDayData) =>
    fetchApi<StudentSuspensionDay>(`${STUDENT_SUSPENSIONS_PATH}/${id}/days/${date}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  postNote: (id: string, body: string) =>
    fetchApi<StudentSuspensionNote>(`${STUDENT_SUSPENSIONS_PATH}/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  threads: (id: string) => fetchApi<StudentSuspensionThreadEntry[]>(`${STUDENT_SUSPENSIONS_PATH}/${id}/threads`),
  messages: (id: string, teacherId: string) =>
    fetchApi<StudentSuspensionMessage[]>(`${threadPath(id, teacherId)}/messages`),
  postMessage: async (id: string, teacherId: string, body: string, files: File[]) => {
    const form = new FormData();
    form.append('body', body);
    files.forEach(f => form.append('files', f, f.name));
    const res = await fetch(`${API_BASE_URL}${threadPath(id, teacherId)}/messages`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return res.json() as Promise<StudentSuspensionMessage>;
  },
  markThreadRead: (id: string, teacherId: string) =>
    fetchApi<{ ok: true }>(`${threadPath(id, teacherId)}/read`, { method: 'POST' }),
};

/** Fetched as a blob: the endpoint is auth-gated, so a plain <a href> won't carry the cookie. */
export async function downloadStudentSuspensionAttachment(
  id: string,
  teacherId: string,
  attachment: StudentSuspensionAttachment
): Promise<void> {
  const res = await fetch(studentSuspensionAttachmentUrl(id, teacherId, attachment.id), { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to download attachment');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = attachment.originalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
